// Recital auto-scheduler — pure, deterministic running-order proposer.
//
// Given a bag of dances (with cast + style + length) and a show's scheduling
// settings, propose a sequence_index ordering that:
//   - honours pinned positions (an opener / finale the admin locked),
//   - gives every dancer enough backstage rest between their dances
//     (the costume-change constraint),
//   - avoids running too many same-style dances back-to-back,
//   - reads as a varied, well-paced show.
//
// This module is intentionally self-contained and side-effect free so the UI
// can call it on demand (and an edge function could too). It REUSES the
// existing lineup primitives rather than reinventing them:
//   - cascadeShowTimeline()  — to turn an order into start/end times.
//   - detectLineupConflicts() — to score an order (quick_change == a
//                               costume-change rest violation).
// The resulting order is meant to be persisted via the apply_lineup_order
// RPC (see supabase/migrations/0009_sprint4_fixes.sql), which re-runs the
// same time cascade DB-side.
//
// Determinism is a hard requirement: the same input MUST yield the same
// output. Every tie-break therefore ends in a stable comparison on dance id,
// and no wall-clock / Math.random is used. The "time budget" is measured
// against a caller-injectable clock (defaults to Date.now) purely to bound
// the local-search loop on large shows; the *result* for a given input is
// independent of how much budget was spent because the search only ever
// keeps strictly-better candidates discovered in a fixed deterministic order.

import { cascadeShowTimeline, type CascadeDance } from './timeCascade';
import { detectLineupConflicts, type DancerFamilyRef, type LineupDanceWithCast } from './conflicts';

// ── Public type contract ────────────────────────────────────────────────────

export interface AutoScheduleDance {
  dance_id: string;
  /** Choreography style, e.g. "Jazz". Empty / missing → treated as 'Unknown'. */
  style?: string | null | undefined;
  /** Length in seconds (drives the timeline + thin-margin scoring). */
  length_seconds: number;
  /** Dancer ids cast in this dance. */
  cast_dancer_ids: string[];
  /**
   * Optional locked position (1-based sequence_index). Two dances may not pin
   * the same slot, and a pin must be within 1..dances.length.
   */
  pinned_sequence_index?: number | null;
  /** Per-dance costume-change rest override (seconds); falls back to settings. */
  costume_change_seconds_override?: number | null;
}

export interface AutoScheduleSettings {
  /** Show start time (ISO with offset) — passed straight to the time cascade. */
  performance_at: string;
  /** Gap between dances in seconds. */
  default_gap_seconds: number;
  /** Insert an intermission AFTER this 1-based sequence_index (null = none). */
  intermission_after_sequence: number | null;
  /** Intermission length in minutes. */
  intermission_minutes: number;
  /** Minimum backstage rest a dancer needs between two of their dances. */
  costume_change_seconds: number;
  /** Cap on consecutive same-style dances (null = no cap). */
  max_consecutive_same_style?: number | null;
  /**
   * Soft local-search time budget in ms. The construction + validation phases
   * always run; only the improvement loop is bounded by this. Default 1500.
   */
  time_budget_ms?: number;
}

export type AutoScheduleViolationKind =
  // Hard — the order is not actually performable / valid as-is.
  | 'insufficient_costume_change'
  | 'max_consecutive_style_exceeded'
  | 'impossible_pin'
  | 'data_error'
  // Soft — the order works but could be better.
  | 'thin_margin'
  | 'clustering'
  | 'weak_variety'
  | 'intermission_imbalance';

export interface AutoScheduleViolation {
  kind: AutoScheduleViolationKind;
  severity: 'hard' | 'soft';
  /** Human-readable, studio-facing explanation (who + why). */
  message: string;
  /** Dancer ids involved, when applicable. */
  dancer_ids: string[];
  /** Dance ids involved, when applicable. */
  dance_ids: string[];
}

export interface AutoScheduleResultItem {
  dance_id: string;
  sequence_index: number; // 1-based
  computed_start_time: string; // ISO
  computed_end_time: string; // ISO
}

export interface AutoScheduleResult {
  /** Proposed running order, sequence_index 1..N. */
  order: AutoScheduleResultItem[];
  /** True when no HARD violations remain. */
  feasible: boolean;
  /** All remaining violations (hard first, then soft), each explained. */
  violations: AutoScheduleViolation[];
  /** Lower is better. Weighted hard*BIG + soft penalties. */
  score: number;
  /**
   * Plain-language summary lines. On infeasibility these explain what could
   * not be satisfied and why — never a fake "scheduled successfully".
   */
  explanation: string[];
}

// ── Tuning constants ──────────────────────────────────────────────────────

const DEFAULT_TIME_BUDGET_MS = 1500;
const UNKNOWN_STYLE = 'Unknown';

// Score weights. Hard violations dominate so the search always prefers
// removing them before polishing soft concerns.
const HARD_WEIGHT = 1_000_000;
const SOFT_WEIGHTS: Record<
  Extract<
    AutoScheduleViolationKind,
    'thin_margin' | 'clustering' | 'weak_variety' | 'intermission_imbalance'
  >,
  number
> = {
  thin_margin: 50,
  clustering: 30,
  weak_variety: 20,
  intermission_imbalance: 40,
};

// A rest margin within this factor of the required minimum is "thin" — it
// technically passes the hard costume-change gate but leaves no slack.
const THIN_MARGIN_FACTOR = 1.5;

// ── Internal normalised representation ──────────────────────────────────────

interface NormDance {
  id: string;
  style: string;
  lengthSeconds: number;
  cast: ReadonlySet<string>;
  restSeconds: number; // resolved costume-change requirement for this dance
  pin: number | null; // 1-based or null
}

interface Validation {
  dances: NormDance[];
  byId: Map<string, NormDance>;
  errors: AutoScheduleViolation[]; // data errors + impossible pins (hard)
  /** Per-dancer count, for spreading high-frequency dancers. */
  dancerFreq: Map<string, number>;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function normaliseStyle(style: string | null | undefined): string {
  const s = (style ?? '').trim();
  return s.length > 0 ? s : UNKNOWN_STYLE;
}

function resolveRest(dance: AutoScheduleDance, settings: AutoScheduleSettings): number {
  const override = dance.costume_change_seconds_override;
  if (override !== null && override !== undefined && Number.isFinite(override) && override >= 0) {
    return override;
  }
  return Math.max(0, settings.costume_change_seconds);
}

/**
 * Validate raw input: duplicate ids, invalid pins, bad data. Produces the
 * normalised dance list plus any HARD data/pin violations. Never throws —
 * everything surfaces as a violation so the caller gets an explanation.
 */
function validate(dances: AutoScheduleDance[], settings: AutoScheduleSettings): Validation {
  const errors: AutoScheduleViolation[] = [];
  const byId = new Map<string, NormDance>();
  const dancerFreq = new Map<string, number>();
  const n = dances.length;

  // Detect duplicate dance ids (a data error — can't place a dance twice).
  const idCounts = new Map<string, number>();
  for (const d of dances) {
    idCounts.set(d.dance_id, (idCounts.get(d.dance_id) ?? 0) + 1);
  }

  const pinSlots = new Map<number, string[]>(); // slot → dance ids claiming it

  const norm: NormDance[] = [];
  const seenForNorm = new Set<string>();
  for (const d of dances) {
    if ((idCounts.get(d.dance_id) ?? 0) > 1) {
      if (!seenForNorm.has(d.dance_id)) {
        errors.push({
          kind: 'data_error',
          severity: 'hard',
          message: `Dance "${d.dance_id}" appears more than once in the input; each dance can only be scheduled once.`,
          dancer_ids: [],
          dance_ids: [d.dance_id],
        });
      }
      seenForNorm.add(d.dance_id);
      // Skip the duplicate copies entirely (keep the first occurrence below).
      if (byId.has(d.dance_id)) continue;
    }

    const cast = new Set(d.cast_dancer_ids ?? []);
    for (const dancer of cast) {
      dancerFreq.set(dancer, (dancerFreq.get(dancer) ?? 0) + 1);
    }

    let pin: number | null = null;
    const rawPin = d.pinned_sequence_index;
    if (rawPin !== null && rawPin !== undefined) {
      if (!Number.isInteger(rawPin) || rawPin < 1 || rawPin > n) {
        errors.push({
          kind: 'impossible_pin',
          severity: 'hard',
          message: `Dance "${d.dance_id}" is pinned to position ${rawPin}, which is outside the valid range 1..${n}.`,
          dancer_ids: [],
          dance_ids: [d.dance_id],
        });
      } else {
        pin = rawPin;
        const claimants = pinSlots.get(pin) ?? [];
        claimants.push(d.dance_id);
        pinSlots.set(pin, claimants);
      }
    }

    if (!Number.isFinite(d.length_seconds) || d.length_seconds < 0) {
      errors.push({
        kind: 'data_error',
        severity: 'hard',
        message: `Dance "${d.dance_id}" has an invalid length (${d.length_seconds}).`,
        dancer_ids: [],
        dance_ids: [d.dance_id],
      });
    }

    const nd: NormDance = {
      id: d.dance_id,
      style: normaliseStyle(d.style),
      lengthSeconds: Number.isFinite(d.length_seconds) ? Math.max(0, d.length_seconds) : 0,
      cast,
      restSeconds: resolveRest(d, settings),
      pin,
    };
    byId.set(nd.id, nd);
    norm.push(nd);
  }

  // Two dances pinned to the same slot is an impossible pin.
  for (const [slot, claimants] of pinSlots) {
    if (claimants.length > 1) {
      errors.push({
        kind: 'impossible_pin',
        severity: 'hard',
        message: `Multiple dances are pinned to position ${slot}: ${claimants.join(', ')}. Only one dance can occupy a slot.`,
        dancer_ids: [],
        dance_ids: [...claimants],
      });
    }
  }

  return { dances: norm, byId, errors, dancerFreq };
}

// ── Timeline + scoring ──────────────────────────────────────────────────────

/** Build the family map detectLineupConflicts needs (each dancer is their own
 * "family" so the sibling rule never fires — we only want quick_change here). */
function castFamily(dances: NormDance[]): Map<string, DancerFamilyRef> {
  const map = new Map<string, DancerFamilyRef>();
  for (const d of dances) {
    for (const dancer of d.cast) {
      if (!map.has(dancer)) {
        map.set(dancer, { dancer_id: dancer, family_id: dancer, display_name: dancer });
      }
    }
  }
  return map;
}

function buildTimeline(order: NormDance[], settings: AutoScheduleSettings): LineupDanceWithCast[] {
  const cascadeInput: CascadeDance[] = order.map((d, i) => ({
    dance_id: d.id,
    sequence_index: i + 1,
    length_seconds: d.lengthSeconds,
  }));
  const timed = cascadeShowTimeline(
    {
      performance_at: settings.performance_at,
      default_gap_seconds: settings.default_gap_seconds,
      intermission_after_sequence: settings.intermission_after_sequence,
      intermission_minutes: settings.intermission_minutes,
    },
    cascadeInput,
  );
  const timeById = new Map(timed.map((t) => [t.dance_id, t]));
  return order.map((d, i) => {
    const t = timeById.get(d.id)!;
    return {
      dance_id: d.id,
      sequence_index: i + 1,
      computed_start_time: t.computed_start_time,
      computed_end_time: t.computed_end_time,
      cast_dancer_ids: [...d.cast],
    };
  });
}

interface Evaluation {
  violations: AutoScheduleViolation[];
  score: number;
  hardCount: number;
}

/**
 * Score an order. Hard violations come from:
 *   - costume-change rest (a quick_change shorter than the dance's required
 *     rest — uses detectLineupConflicts with each dancer's resolved rest),
 *   - max-consecutive-style runs.
 * Soft: thin rest margins, style clustering, weak overall variety, and
 * intermission half imbalance.
 */
function evaluate(
  order: NormDance[],
  settings: AutoScheduleSettings,
  baseErrors: AutoScheduleViolation[],
): Evaluation {
  const violations: AutoScheduleViolation[] = [...baseErrors];

  const timeline = buildTimeline(order, settings);
  const family = castFamily(order);

  // Costume-change rest: run the conflict detector at the *largest* required
  // rest so we get every candidate pair, then keep only pairs that actually
  // violate the per-dancer requirement. restByDancer = max over their dances.
  const restByDancer = new Map<string, number>();
  for (const d of order) {
    for (const dancer of d.cast) {
      restByDancer.set(dancer, Math.max(restByDancer.get(dancer) ?? 0, d.restSeconds));
    }
  }
  const maxRest = Math.max(0, ...[...restByDancer.values()], settings.costume_change_seconds);

  // Query the detector at the *thin-margin ceiling* so it surfaces not only
  // hard sub-threshold gaps but also gaps that merely lack comfortable slack
  // (required ≤ gap < required × THIN_MARGIN_FACTOR). We then classify each
  // returned pair against that dancer's own required rest.
  const conflicts = detectLineupConflicts(timeline, family, {
    quickChangeMinSeconds: Math.ceil(maxRest * THIN_MARGIN_FACTOR),
    siblingAdjacentWindow: 0, // disable the sibling rule
  });

  for (const c of conflicts) {
    if (c.kind !== 'quick_change') continue;
    const dancer = c.dancer_ids[0]!;
    const required = restByDancer.get(dancer) ?? settings.costume_change_seconds;
    const gap = c.gap_seconds ?? 0;
    if (gap < required) {
      violations.push({
        kind: 'insufficient_costume_change',
        severity: 'hard',
        message: `Dancer ${dancer} only has ${gap}s to change between dances ${c.dance_ids.join(' → ')}, but needs at least ${required}s.`,
        dancer_ids: [dancer],
        dance_ids: c.dance_ids,
      });
    } else if (gap < required * THIN_MARGIN_FACTOR) {
      violations.push({
        kind: 'thin_margin',
        severity: 'soft',
        message: `Dancer ${dancer} has only ${gap}s to change between dances ${c.dance_ids.join(' → ')} (comfortable target ${Math.round(required * THIN_MARGIN_FACTOR)}s).`,
        dancer_ids: [dancer],
        dance_ids: c.dance_ids,
      });
    }
  }

  // Max consecutive same style (hard) + clustering (soft).
  const cap = settings.max_consecutive_same_style;
  let runStyle: string | null = null;
  let runStart = 0;
  const flushRun = (endExclusive: number) => {
    if (runStyle === null) return;
    const runLen = endExclusive - runStart;
    const runIds = order.slice(runStart, endExclusive).map((d) => d.id);
    if (cap !== null && cap !== undefined && runLen > cap) {
      violations.push({
        kind: 'max_consecutive_style_exceeded',
        severity: 'hard',
        message: `${runLen} "${runStyle}" dances run back-to-back (cap is ${cap}): ${runIds.join(', ')}.`,
        dancer_ids: [],
        dance_ids: runIds,
      });
    } else if (runLen >= 3) {
      // Soft clustering signal even when under (or without) a hard cap.
      violations.push({
        kind: 'clustering',
        severity: 'soft',
        message: `${runLen} "${runStyle}" dances cluster together: ${runIds.join(', ')}.`,
        dancer_ids: [],
        dance_ids: runIds,
      });
    }
  };
  for (let i = 0; i < order.length; i++) {
    const s = order[i]!.style;
    if (s !== runStyle) {
      flushRun(i);
      runStyle = s;
      runStart = i;
    }
  }
  flushRun(order.length);

  // Weak variety (soft): adjacent same-style transitions beyond what's
  // unavoidable given the style multiset. If every neighbour shares a style
  // we have done a poor job interleaving.
  if (order.length >= 2) {
    let sameNeighbours = 0;
    for (let i = 1; i < order.length; i++) {
      if (order[i]!.style === order[i - 1]!.style) sameNeighbours++;
    }
    const styleCounts = new Map<string, number>();
    for (const d of order) styleCounts.set(d.style, (styleCounts.get(d.style) ?? 0) + 1);
    // Unavoidable same-neighbour count: if the most frequent style has m of N,
    // at best it forces max(0, m - (N - m) - 1) same-style adjacencies.
    const maxStyle = Math.max(...styleCounts.values());
    const unavoidable = Math.max(0, maxStyle - (order.length - maxStyle) - 1);
    if (sameNeighbours > unavoidable) {
      violations.push({
        kind: 'weak_variety',
        severity: 'soft',
        message: `${sameNeighbours} back-to-back same-style transitions (about ${unavoidable} are unavoidable given the style mix) — the order could interleave styles better.`,
        dancer_ids: [],
        dance_ids: [],
      });
    }
  }

  // Intermission imbalance (soft): if an intermission is configured, the two
  // halves should be within ~25% of each other in runtime.
  const interAfter = settings.intermission_after_sequence;
  if (
    interAfter !== null &&
    interAfter !== undefined &&
    interAfter >= 1 &&
    interAfter < order.length
  ) {
    let firstHalf = 0;
    let secondHalf = 0;
    for (let i = 0; i < order.length; i++) {
      if (i < interAfter) firstHalf += order[i]!.lengthSeconds;
      else secondHalf += order[i]!.lengthSeconds;
    }
    const total = firstHalf + secondHalf;
    if (total > 0) {
      const skew = Math.abs(firstHalf - secondHalf) / total;
      if (skew > 0.25) {
        violations.push({
          kind: 'intermission_imbalance',
          severity: 'soft',
          message: `Act runtimes are uneven: ${Math.round(firstHalf / 60)}min before vs ${Math.round(secondHalf / 60)}min after the intermission.`,
          dancer_ids: [],
          dance_ids: [],
        });
      }
    }
  }

  const hardCount = violations.filter((v) => v.severity === 'hard').length;
  let score = hardCount * HARD_WEIGHT;
  for (const v of violations) {
    if (v.severity === 'soft') {
      score += SOFT_WEIGHTS[v.kind as keyof typeof SOFT_WEIGHTS] ?? 10;
    }
  }

  return { violations, score, hardCount };
}

// ── Greedy construction ─────────────────────────────────────────────────────

/**
 * A candidate's ranking key for a given open slot. Compared lexicographically;
 * smaller wins. All components are numbers except the final id tie-break.
 */
type ConstructKey = readonly [
  exceedPenalty: number, // 0 preferred (does not bust the style cap)
  restKey: number, // -restProxy: larger rest (more-negative) preferred
  sameStyle: number, // 0 preferred (differs from previous style)
  freqSum: number, // lower preferred (spread high-frequency dancers)
  id: string, // final deterministic tie-break
];

function compareConstructKeys(a: ConstructKey, b: ConstructKey): number {
  for (let i = 0; i < 4; i++) {
    const an = a[i] as number;
    const bn = b[i] as number;
    if (an !== bn) return an - bn;
  }
  return a[4].localeCompare(b[4]);
}

/**
 * Build an initial order: honour pins, then greedily place the remaining
 * dances into the open slots. At each open slot pick the dance whose
 * ConstructKey sorts first:
 *   1. would NOT exceed max_consecutive_same_style with the placed prefix,
 *   2. prefers the cast that has rested longest (largest slot distance since
 *      any of its dancers last danced) — reduces costume-change pressure,
 *   3. tie-break: a style different from the previous slot (variety),
 *   4. tie-break: spread high-frequency dancers (lower summed dancer freq),
 *   5. tie-break: dance id, for full determinism.
 */
function greedyConstruct(
  dances: NormDance[],
  settings: AutoScheduleSettings,
  dancerFreq: ReadonlyMap<string, number>,
): NormDance[] {
  const n = dances.length;
  const slots: (NormDance | null)[] = Array.from({ length: n }, () => null);

  // Place pins first. Validation already flagged out-of-range / duplicate-slot
  // pins as hard violations; here we defend anyway so construction never
  // crashes on bad data: skip out-of-range pins, and if two dances claim the
  // same slot the first (by id, for determinism) wins and the loser falls back
  // into the free pool. The order is still returned (best-effort), with the
  // impossible_pin violation already attached upstream.
  const pinned = new Set<string>();
  const pinClaim = [...dances]
    .filter((d) => d.pin !== null && d.pin >= 1 && d.pin <= n)
    .sort((a, b) => a.id.localeCompare(b.id));
  for (const d of pinClaim) {
    const idx = d.pin! - 1;
    if (slots[idx] === null) {
      slots[idx] = d;
      pinned.add(d.id);
    }
  }

  // Deterministic candidate pool; consumed as we fill slots.
  const remaining = dances
    .filter((d) => !pinned.has(d.id))
    .sort((a, b) => a.id.localeCompare(b.id));

  // Per dancer, the slot index of their most recent placement — a cheap
  // rest proxy (slot distance) during construction.
  const lastSlotByDancer = new Map<string, number>();
  const seedLastSlot = (slotIdx: number, d: NormDance) => {
    for (const dancer of d.cast) lastSlotByDancer.set(dancer, slotIdx);
  };
  for (let i = 0; i < n; i++) {
    if (slots[i]) seedLastSlot(i, slots[i]!);
  }

  // Precompute per-cast summed frequency once (DRY: avoid O(n) rescans).
  const freqSumByDance = new Map<string, number>();
  for (const d of dances) {
    let sum = 0;
    for (const dancer of d.cast) sum += dancerFreq.get(dancer) ?? 0;
    freqSumByDance.set(d.id, sum);
  }

  const cap = settings.max_consecutive_same_style ?? Infinity;

  const keyFor = (
    cand: NormDance,
    slot: number,
    prev: NormDance | null,
    prevRun: number,
  ): ConstructKey => {
    const wouldExceed = prev !== null && cand.style === prev.style && prevRun + 1 > cap;
    let minDist = Infinity;
    for (const dancer of cand.cast) {
      const last = lastSlotByDancer.get(dancer);
      if (last !== undefined) minDist = Math.min(minDist, slot - last);
    }
    const restProxy = minDist === Infinity ? n : minDist; // unseen → maximally rested
    const sameStyle = prev !== null && cand.style === prev.style ? 1 : 0;
    return [wouldExceed ? 1 : 0, -restProxy, sameStyle, freqSumByDance.get(cand.id) ?? 0, cand.id];
  };

  for (let slot = 0; slot < n; slot++) {
    if (slots[slot]) continue;
    const prev = slot > 0 ? slots[slot - 1] : null;

    // Consecutive same-style run length ending at prev.
    let prevRun = 0;
    if (prev) {
      for (let k = slot - 1; k >= 0 && slots[k]?.style === prev.style; k--) {
        prevRun++;
      }
    }

    let bestIdx = -1;
    let bestKey: ConstructKey | null = null;
    for (let r = 0; r < remaining.length; r++) {
      const key = keyFor(remaining[r]!, slot, prev ?? null, prevRun);
      if (bestKey === null || compareConstructKeys(key, bestKey) < 0) {
        bestKey = key;
        bestIdx = r;
      }
    }

    // bestIdx is always set while open slots remain (remaining is non-empty).
    const [chosen] = remaining.splice(bestIdx, 1);
    slots[slot] = chosen!;
    seedLastSlot(slot, chosen!);
  }

  return slots.map((s) => s!);
}

// ── Local search ────────────────────────────────────────────────────────────

/** Indices that are pinned and must not move during local search. */
function pinnedSlots(order: NormDance[]): Set<number> {
  const set = new Set<number>();
  order.forEach((d, i) => {
    if (d.pin !== null) set.add(i);
  });
  return set;
}

/**
 * Deterministic hill-climb. Repeatedly scan all pairwise swaps in a fixed
 * (i, j) order; apply the first swap that strictly lowers the score, then
 * restart the scan. Stop when a full pass finds no improvement OR the time
 * budget elapses. Pinned slots are frozen.
 *
 * Determinism: we only ever accept strictly-better moves discovered in a fixed
 * scan order, so for a given input the *sequence* of accepted moves is fixed.
 * The wall-clock budget is a safety cap for pathological large inputs; it is
 * checked on a fixed cadence (every CHECK_INTERVAL evaluations) so the check
 * cost is negligible. Two runs on the same machine converge identically; a
 * budget cutoff only ever yields a (still valid, still complete) prefix of the
 * same improvement trajectory.
 */
const BUDGET_CHECK_INTERVAL = 256;

function localSearch(
  initial: NormDance[],
  settings: AutoScheduleSettings,
  baseErrors: AutoScheduleViolation[],
  now: () => number,
): { order: NormDance[]; evaluation: Evaluation } {
  let order = [...initial];
  let evalResult = evaluate(order, settings, baseErrors);
  const frozen = pinnedSlots(order);
  const n = order.length;
  const budget = settings.time_budget_ms ?? DEFAULT_TIME_BUDGET_MS;
  const deadline = now() + budget;

  // Nothing to improve if already perfect or trivially small.
  if (n < 2 || evalResult.score === 0) return { order, evaluation: evalResult };

  let evalsSinceCheck = 0;
  let improved = true;
  while (improved) {
    improved = false;
    for (let i = 0; i < n && !improved; i++) {
      if (frozen.has(i)) continue;
      for (let j = i + 1; j < n; j++) {
        if (frozen.has(j)) continue;

        // Coarse, cheap budget check so a huge show cannot blow past the cap.
        if (++evalsSinceCheck >= BUDGET_CHECK_INTERVAL) {
          evalsSinceCheck = 0;
          if (now() > deadline) return { order, evaluation: evalResult };
        }

        // Try swapping slots i and j.
        const candidate = [...order];
        const tmp = candidate[i]!;
        candidate[i] = candidate[j]!;
        candidate[j] = tmp;
        const candEval = evaluate(candidate, settings, baseErrors);
        if (candEval.score < evalResult.score) {
          order = candidate;
          evalResult = candEval;
          improved = true;
          if (evalResult.score === 0) return { order, evaluation: evalResult };
          break; // restart the scan from the improved order (first-improvement)
        }
      }
    }
  }

  return { order, evaluation: evalResult };
}

// ── Explanation builder ─────────────────────────────────────────────────────

function buildExplanation(
  feasible: boolean,
  violations: AutoScheduleViolation[],
  danceCount: number,
): string[] {
  const lines: string[] = [];
  if (danceCount === 0) {
    return ['No dances to schedule.'];
  }
  const hard = violations.filter((v) => v.severity === 'hard');
  const soft = violations.filter((v) => v.severity === 'soft');

  if (feasible) {
    lines.push(
      `Scheduled ${danceCount} dance${danceCount === 1 ? '' : 's'} with no hard conflicts.`,
    );
    if (soft.length > 0) {
      lines.push(
        `${soft.length} soft suggestion${soft.length === 1 ? '' : 's'} remain (the order is performable but could be smoother):`,
      );
      for (const v of soft) lines.push(`  • ${v.message}`);
    }
  } else {
    lines.push(
      `Could not produce a fully conflict-free order for ${danceCount} dances. ${hard.length} hard issue${hard.length === 1 ? '' : 's'} could not be resolved — this is the best order found:`,
    );
    for (const v of hard) lines.push(`  • ${v.message}`);
    if (soft.length > 0) {
      lines.push(`Additional soft suggestions:`);
      for (const v of soft) lines.push(`  • ${v.message}`);
    }
    lines.push(
      'Try relaxing a pin, raising the costume-change window, increasing the max-consecutive-style cap, or splitting heavy cast overlaps across more dances.',
    );
  }
  return lines;
}

function orderViolations(violations: AutoScheduleViolation[]): AutoScheduleViolation[] {
  // Hard before soft; stable within each group (preserves detection order).
  return [...violations].sort((a, b) => {
    if (a.severity !== b.severity) return a.severity === 'hard' ? -1 : 1;
    return 0;
  });
}

// ── Public entry point ──────────────────────────────────────────────────────

/**
 * Propose a deterministic running order for `dances` under `settings`.
 *
 * Always returns a complete order (every input dance placed exactly once),
 * even when infeasible — in which case `feasible` is false and `explanation`
 * states what could not be satisfied. Never throws on bad data; data problems
 * surface as hard `data_error` / `impossible_pin` violations.
 */
export function autoScheduleShow(
  dances: AutoScheduleDance[],
  settings: AutoScheduleSettings,
  clock: () => number = Date.now,
): AutoScheduleResult {
  const { dances: norm, errors, dancerFreq } = validate(dances, settings);

  if (norm.length === 0) {
    return {
      order: [],
      feasible: errors.length === 0,
      violations: orderViolations(errors),
      score: errors.filter((e) => e.severity === 'hard').length * HARD_WEIGHT,
      explanation: buildExplanation(errors.length === 0, errors, 0),
    };
  }

  const initial = greedyConstruct(norm, settings, dancerFreq);
  const { order, evaluation } = localSearch(initial, settings, errors, clock);

  const timeline = buildTimeline(order, settings);
  const orderItems: AutoScheduleResultItem[] = timeline.map((t) => ({
    dance_id: t.dance_id,
    sequence_index: t.sequence_index,
    computed_start_time: t.computed_start_time,
    computed_end_time: t.computed_end_time,
  }));

  const feasible = evaluation.hardCount === 0;
  const violations = orderViolations(evaluation.violations);

  return {
    order: orderItems,
    feasible,
    violations,
    score: evaluation.score,
    explanation: buildExplanation(feasible, violations, norm.length),
  };
}
